import React, { useCallback, useEffect } from 'react'
import isBoolean from 'lodash/isBoolean'
import compose from 'recompose/compose'
import { useController, useFormContext } from 'react-hook-form'
import { connect as connectStaticText } from '../StaticText/connect'
import {
  TYPES, getTag, getClass, getLabelId, getInputId,
} from '../FormInput.v2/utils'
import { useLegacyFieldValidator } from './useLegacyFormField'
import type {
  CheckboxLabel,
  FormFieldStaticTextProps,
  FormInputAppearanceProps,
  LegacyValidationProps,
} from './formFieldTypes'

export const STYLES = {
  SECONDARY: 'SECONDARY',
  PRIMARY: 'PRIMARY',
} as const

export type CheckboxProps =
  FormFieldStaticTextProps
  & FormInputAppearanceProps
  & LegacyValidationProps & {
  name: string
  label: CheckboxLabel
  asSlider?: boolean
  onChange?: (name: string, checked: boolean, value: string) => boolean | void
  onBlur?: (name: string, checked: boolean, value: string) => boolean | void
  htmlValue?: string
  tabIndex?: string
  setValueOnKeyUp?: boolean
  style?: typeof STYLES.SECONDARY | typeof STYLES.PRIMARY
  hide?: boolean
  value?: boolean
}

function Checkbox (props: CheckboxProps) {
  const {
    staticText,
    name,
    label,
    note,
    asSlider,
    required,
    validations,
    validationErrors,
    validationError,
    validationErrorFormat,
    tabIndex,
    setValueOnKeyUp,
    htmlValue,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    value: valueProp,
  } = props

  const { control, setValue } = useFormContext()
  const requiredMessage = staticText.getIn(['data', 'required'])

  const stringValidate = useLegacyFieldValidator(staticText, {
    validations,
    required: false,
    validationErrors,
    validationError,
    validationErrorFormat,
  })

  const validate = useCallback((value: unknown) => {
    if (required && !value) {
      return requiredMessage != null ? String(requiredMessage) : 'This field is required'
    }
    const result = stringValidate(value ? '1' : '')
    return result === true ? true : result
  }, [required, requiredMessage, stringValidate])

  const {
    field,
    fieldState,
  } = useController({
    name,
    control,
    rules: { validate },
    defaultValue: valueProp ?? false,
  })

  const checked = valueProp !== undefined ? valueProp : field.value

  useEffect(() => {
    if (valueProp !== undefined && valueProp !== field.value) {
      setValue(name, valueProp, { shouldValidate: false })
    }
  }, [valueProp, field.value, name, setValue])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { currentTarget } = e
    const { value, checked: nextChecked } = currentTarget
    let modifiedValue = nextChecked
    if (onChangeProp) {
      const callbackResult = onChangeProp(name, nextChecked, value)
      if (isBoolean(callbackResult)) {
        modifiedValue = callbackResult
      }
    }
    field.onChange(modifiedValue)
    if (valueProp !== undefined) {
      setValue(name, modifiedValue, { shouldValidate: true })
    }
  }, [name, onChangeProp, field, valueProp, setValue])

  const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const { currentTarget } = e
    const { value, checked: blurredChecked } = currentTarget
    if (onBlurProp) {
      const modifiedValue = onBlurProp(name, blurredChecked, value)
      if (isBoolean(modifiedValue)) {
        field.onChange(modifiedValue)
      }
    }
    field.onBlur()
  }, [name, onBlurProp, field])

  const keyUp = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    const keycode = (e.keyCode ? e.keyCode : e.which)
    if (keycode === 32 || keycode === 13) {
      field.onChange(!field.value)
    }
  }

  const error = fieldState.error?.message || null
  const className = getClass(asSlider ? TYPES.SLIDER : TYPES.CHECKBOX, props, error)
  const labelId = getLabelId(name)
  const id = getInputId(name)

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <div className={className}>
      {React.createElement(getTag(TYPES.CHECKBOX), {
        className: 'forminput__input',
        'aria-labelledby': labelId,
        checked,
        required,
        readOnly: props.readonly,
        disabled: props.disabled,
        value: htmlValue,
        type: TYPES.CHECKBOX,
        note,
        name: id,
        onChange,
        onBlur,
        id,
      })}
      <label
        className="forminput__label"
        tabIndex={tabIndex ? Number(tabIndex) : -1}
        htmlFor={id}
        id={labelId}
        onKeyUp={setValueOnKeyUp ? keyUp : undefined}
      >{label}</label>
      {note ? (
        <span
          role="alert"
          aria-live="polite"
          className="forminput__note"
        >{note}</span>
      ) : null}
      {error ? (
        <span
          role="alert"
          aria-live="polite"
          className="forminput__error"
        >{error}</span>
      ) : null}
    </div>
  )
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
}

export default compose(
  connectStaticText({ storeKey: 'formInputV2' }),
)(Checkbox as React.ComponentType<unknown>) as React.ComponentType<Omit<CheckboxProps, 'staticText'>>
